// Global Instructions Rule Applied!

import React, { useCallback, useMemo, useState } from 'react'
import { Alert, Divider, Input, Select } from 'antd'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { faArrowRotateLeft, faTimes } from '@fortawesome/free-solid-svg-icons'
import { toast } from 'react-toastify'
import { showErrorToast } from '../../../core/utils/errorToast.tsx'

import Button from '../../../core/components/Button.jsx'
import { saveReportCustomization } from '../services/documentReportsService.ts'
import type {
  FeedbackCustomizationContext,
  FeedbackCustomizationData
} from '../../../state_manager/data_models/document_report_customization_model.ts'

const { TextArea } = Input

/** One exit-outlook override draft. signal undefined = keep the derived
 *  signal; a row with neither a signal nor a note is dropped on save. */
interface OutlookDraft {
  accountId: string
  signal: string | undefined
  note: string
}

interface FeedbackCustomizeBodyProps {
  context: FeedbackCustomizationContext
  reportCode: string
  accessToken: string | null
  onClose: () => void
  /** Called after a successful save so the parent can refresh the preview. */
  onSaved: () => void
}

const SECTION_TITLE_CLASS = 'shrink-0 mb-1 text-[11px] font-semibold uppercase tracking-wide text-gray-500'

/**
 * The Investor Feedback Report's Customize body: edit the synthesis slide's
 * Watch Flags (same slot semantics as the performance report's highlights)
 * and override the per-company Exit Outlook — the derived signal is
 * mechanical (age × staleness × recency), so the GP's judgment replaces it
 * per company, optionally with a short note that prints beside the chip.
 */
export function FeedbackCustomizeBody({
  context,
  reportCode,
  accessToken,
  onClose,
  onSaved
}: FeedbackCustomizeBodyProps) {
  const [saving, setSaving] = useState(false)

  // Flag drafts hold the EFFECTIVE text (saved override or generated); a slot
  // equal to its generated flag saves as null (= stays live).
  const [flags, setFlags] = useState<string[]>(() => {
    const savedData = context.saved?.data
    return Array.from({ length: context.limits.flag_slots }, (_, i) => {
      const savedSlot = savedData?.flags?.[i]
      if (typeof savedSlot === 'string' && savedSlot.trim()) return savedSlot
      return context.generated.flags[i] ?? ''
    })
  })

  // Outlook drafts are OVERRIDES only — the derived signals already show on
  // the deck; this list carries the companies the GP has said something about.
  const [outlook, setOutlook] = useState<OutlookDraft[]>(() =>
    (context.saved?.data.exit_outlook ?? []).map((entry) => ({
      accountId: entry.account_id,
      signal: entry.signal ?? undefined,
      note: entry.note ?? ''
    }))
  )

  const poolByAccountId = useMemo(
    () => new Map(context.pool.map((entry) => [entry.account_id, entry])),
    [context]
  )

  const addOptions = useMemo(
    () =>
      context.pool
        .filter((entry) => !outlook.some((d) => d.accountId === entry.account_id))
        .map((entry) => ({
          value: entry.account_id,
          label: `${entry.name} (${entry.nav_label})`
        })),
    [context, outlook]
  )

  const signalOptions = useMemo(
    () => context.limits.signals.map((s) => ({ value: s.value, label: s.label })),
    [context]
  )

  const signalLabel = useCallback(
    (value: string | null | undefined) =>
      context.limits.signals.find((s) => s.value === value)?.label,
    [context]
  )

  /** The blob the current drafts amount to — all-generated collapses to the
   *  empty payload, which the backend stores as "clear the row". */
  const draftData: FeedbackCustomizationData = useMemo(() => {
    const generated = context.generated.flags
    const flagSlots = flags.map((text, i) => {
      const trimmed = text.trim()
      if (!trimmed || trimmed === (generated[i] ?? '').trim()) return null
      return trimmed
    })
    const entries = outlook
      .map((d) => ({
        account_id: d.accountId,
        signal: d.signal ?? null,
        note: d.note.trim() || null
      }))
      .filter((d) => d.signal || d.note)
    return {
      flags: flagSlots,
      exit_outlook: entries.length ? entries : null
    }
  }, [context, flags, outlook])

  const handleSave = useCallback(async () => {
    setSaving(true)
    const result = await saveReportCustomization(
      {
        reportCode,
        fundId: context.fund_id,
        periodStart: context.period.start,
        periodEnd: context.period.end,
        data: draftData
      },
      accessToken
    )
    setSaving(false)
    if (!result.success) {
      showErrorToast(result.error || 'Failed to save customization')
      return
    }
    toast.success(
      result.saved ? 'Customization saved' : 'Customization cleared — report is fully generated'
    )
    onSaved()
    onClose()
  }, [context, reportCode, draftData, accessToken, onSaved, onClose])

  const handleResetAll = useCallback(() => {
    setFlags(
      Array.from(
        { length: context.limits.flag_slots },
        (_, i) => context.generated.flags[i] ?? ''
      )
    )
    setOutlook([])
  }, [context])

  const setFlag = useCallback((index: number, value: string) => {
    setFlags((prev) => prev.map((v, i) => (i === index ? value : v)))
  }, [])

  const setEntry = useCallback((accountId: string, patch: Partial<OutlookDraft>) => {
    setOutlook((prev) =>
      prev.map((d) => (d.accountId === accountId ? { ...d, ...patch } : d))
    )
  }, [])

  const removeEntry = useCallback((accountId: string) => {
    setOutlook((prev) => prev.filter((d) => d.accountId !== accountId))
  }, [])

  const addEntry = useCallback((accountId: string) => {
    setOutlook((prev) => [...prev, { accountId, signal: undefined, note: '' }])
  }, [])

  return (
    <div className='flex min-h-0 flex-1 flex-col'>
      <div className='flex shrink-0 flex-wrap items-center justify-between gap-2'>
        <Button
          type='text'
          variant='default'
          size='small'
          icon={<FontAwesomeIcon icon={faArrowRotateLeft} className='text-xs' />}
          onClick={handleResetAll}
        >
          Reset all to generated
        </Button>
        <div className='flex items-center gap-1.5'>
          <Button variant='cancel' size='small' onClick={onClose}>
            Cancel
          </Button>
          <Button
            variant='save'
            size='small'
            loading={saving}
            onClick={() => {
              void handleSave()
            }}
          >
            Save
          </Button>
        </div>
      </div>
      <Divider className='!mt-1 !mb-2 shrink-0' />

      <div className='min-h-0 flex-1 overflow-y-auto pr-1'>
        <Alert
          type='warning'
          showIcon
          className='mb-2'
          message='Edited text is frozen'
          description='Generated flags embed live figures and keep up with the data. Text you edit stays exactly as written — if the underlying data changes later, an edited flag can contradict the computed slides.'
        />
        {context.saved && (
          <div className='mb-2 text-xs text-gray-500'>
            Last edited
            {context.saved.modified_by_name ? ` by ${context.saved.modified_by_name}` : ''}
            {' on '}
            {new Date(context.saved.date_modified).toLocaleDateString('en-US')}
            . Shared — everyone running this report for this fund and date sees these edits.
          </div>
        )}

        <div className={SECTION_TITLE_CLASS}>Watch Flags — {context.period.label}</div>
        <div className='mb-1 text-xs text-gray-500'>
          Up to five bullets on the synthesis slide. Use **double asterisks** to bold a name or
          figure.
        </div>
        {flags.map((text, i) => {
          const generated = context.generated.flags[i] ?? ''
          const isCustom = text.trim() !== generated.trim()
          return (
            <div key={i} className='mb-2'>
              <div className='mb-0.5 flex items-center justify-between'>
                <span className='text-xs font-medium text-gray-600'>Flag {i + 1}</span>
                {isCustom && (
                  <Button
                    type='text'
                    variant='default'
                    size='small'
                    className='!h-5 !px-1 !text-[11px]'
                    onClick={() => setFlag(i, generated)}
                  >
                    Reset to generated
                  </Button>
                )}
              </div>
              <TextArea
                value={text}
                onChange={(e) => setFlag(i, e.target.value)}
                maxLength={context.limits.flag_max}
                showCount
                autoSize={{ minRows: 2, maxRows: 4 }}
                placeholder={generated || 'No generated flag for this slot'}
              />
            </div>
          )
        })}

        <Divider className='!my-2' />
        <div className={SECTION_TITLE_CLASS}>Exit Outlook Overrides</div>
        <div className='mb-1 text-xs text-gray-500'>
          The deck derives a signal per position (age, valuation staleness, financing recency).
          Add a company to replace its derived signal with your own call, or attach a short
          note that prints beside the chip.
        </div>
        <Select
          className='mb-2 w-full'
          size='small'
          showSearch
          optionFilterProp='label'
          placeholder='Add company override'
          value={null}
          options={addOptions}
          onChange={(value) => value && addEntry(value)}
        />
        {outlook.map((entry) => {
          const poolEntry = poolByAccountId.get(entry.accountId)
          const derivedLabel = signalLabel(poolEntry?.derived_signal)
          return (
            <div key={entry.accountId} className='mb-2 rounded border border-gray-200 p-2'>
              <div className='mb-1 flex items-center justify-between gap-2'>
                <span className='min-w-0 flex-1 truncate text-xs font-medium text-gray-600'>
                  {poolEntry?.name ?? entry.accountId}
                  {poolEntry ? ` · ${poolEntry.nav_label}` : ''}
                </span>
                <Button
                  type='text'
                  variant='default'
                  size='small'
                  className='!h-5 !px-1 !text-[11px]'
                  icon={<FontAwesomeIcon icon={faTimes} className='text-xs' />}
                  onClick={() => removeEntry(entry.accountId)}
                >
                  Remove
                </Button>
              </div>
              {poolEntry && (
                <div className='mb-1 text-[11px] text-gray-500'>{poolEntry.evidence}</div>
              )}
              <Select
                className='mb-1.5 w-full'
                size='small'
                allowClear
                placeholder={
                  derivedLabel ? `Signal — derived: ${derivedLabel}` : 'Signal — none derived'
                }
                value={entry.signal}
                options={signalOptions}
                onChange={(value) => setEntry(entry.accountId, { signal: value ?? undefined })}
              />
              <TextArea
                value={entry.note}
                onChange={(e) => setEntry(entry.accountId, { note: e.target.value })}
                maxLength={context.limits.note_max}
                showCount
                autoSize={{ minRows: 1, maxRows: 3 }}
                placeholder='Note (prints beside the signal chip)'
              />
            </div>
          )
        })}
      </div>
    </div>
  )
}
